'use client';

import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';

export type TicketShapeConfig = {
    fillColorHex?: string;
    fillColorOpacity?: number;
    cornerRadius?: number;
    footerBackgroundColor?: string;
};

type TicketShapeProps = {
    children?: React.ReactNode;
    className?: string;
    config?: TicketShapeConfig;
    onClick?: () => void;
    footer?: string | React.JSX.Element;
    circlesAmount?: number;
    style?: React.CSSProperties;
};

const drawTicket = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    backgroundColor: string | undefined,
    notchesAmount = 3
) => {
    if (!width || !height) {
        return;
    }
    ctx.clearRect(0, 0, width, height);

    const scale = 4; // Scale factor for higher resolution rendering

    // Scale canvas for high-DPI displays
    ctx.canvas.width = width * scale;
    ctx.canvas.height = height * scale;
    ctx.scale(scale, scale);

    // Draw base shape. Without a color there is nothing to punch the notches out of — bail instead
    // of filling, because assigning an unparseable `fillStyle` (`''`, or a `var(--x)` reference) is
    // silently ignored and would leave the canvas' default black covering the ticket.
    if (!backgroundColor) {
        return;
    }
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    ctx.globalAlpha = 1;

    // Draw notches
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'black';
    const padding = Math.floor(height / 10);
    const notchSize = Math.floor((height - padding * 2) / ((notchesAmount - 1) * 1.6 + 1));
    const notchSpacing = notchSize * 0.6;

    for (let i = 0; i < notchesAmount; i++) {
        const notchY = padding + i * (notchSize + notchSpacing);
        ctx.beginPath();
        ctx.arc(width, notchY + notchSize / 2, notchSize / 2, 0, Math.PI * 2);
        ctx.fill();
    }

    // Reset global composite operation
    ctx.globalCompositeOperation = 'source-over';
};

const TicketShape: React.FC<TicketShapeProps> = ({
    children,
    className,
    config,
    onClick,
    footer,
    circlesAmount,
    style
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>({ width: 0, height: 0 });
    const backgroundColor = config?.fillColorHex
        ? config.fillColorHex.length > 7
            ? config.fillColorHex
            : withOpacity(config.fillColorHex, config.fillColorOpacity)
        : undefined;
    const updateDimensions = () => {
        if (contentRef.current) {
            const width = contentRef.current.offsetWidth;
            const height = contentRef.current.offsetHeight;
            setDimensions({ width, height });
        }
    };

    useEffect(() => {
        updateDimensions();

        const observer = new ResizeObserver(updateDimensions);

        const contentEl = contentRef.current;
        if (contentEl) observer.observe(contentEl);

        return () => {
            if (contentEl) observer.unobserve(contentEl);
        };
    }, []);

    useEffect(() => {
        if (dimensions && canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            if (ctx) {
                drawTicket(ctx, dimensions.width, dimensions.height, backgroundColor, circlesAmount);
            }
        }
    }, [dimensions, backgroundColor, circlesAmount]);

    const ticketShapeStyle = {
        ...style,
        '--ticket-radius': `${config?.cornerRadius ?? 8}px`
    };
    return (
        <div
            className={cn(className, 'w-full h-full flex flex-col ticket overflow-clip rounded-[var(--ticket-radius)]')}
            style={ticketShapeStyle as React.CSSProperties}
            onClick={onClick}>
            <div
                className="w-full h-full py-1.5 relative"
                ref={contentRef}>
                <canvas
                    ref={canvasRef}
                    className="absolute top-0 left-0 w-full h-full z-0"
                />
                {children}
            </div>
            {footer && (
                <div
                    className={cn('px-10 overflow-hidden h-[21px] shrink-0 flex items-center')}
                    style={{
                        background: [
                            config?.footerBackgroundColor
                                ? `linear-gradient(0deg, ${config.footerBackgroundColor} 0%, ${config.footerBackgroundColor} 100%)`
                                : undefined,
                            backgroundColor
                        ]
                            .filter(Boolean)
                            .join(', ')
                    }}>
                    {footer}
                </div>
            )}
        </div>
    );
};

export default TicketShape;
